import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, memo } from 'react';
import { connect } from 'react-redux';
import { fromJS, List, Map } from 'immutable';
import moment from 'moment';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { handleGetUsersSuggestion } from '../../actions/popup';
import SetNextActionsPopup from '../../components/Popup/SetNextActionsPopup';
import { showInformationCustomTitle } from '../../utils/MessagePopup';
import { targetStatusesWithDefault } from '../../utils/targetStatuses';
import { fetchTargetStatuses } from '../../actions/statuses';
import { fetchEventActivities } from '../../actions/eventActivities';
import { usePopupContext } from '../../contexts/PopupContext';

const SetNextActionsPopupContainer = ({
  popup,
  user,
  activities,
  fetchTargetStatuses,
  fetchEventActivities,
  handleGetUsersSuggestion,
}) => {
  const { closePopup, openPopup, updatePopupFormData, startPopupLoading, endPopupLoading } = usePopupContext();
  const [statuses, setStatuses] = useState(List());
  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    const userName = user.get('userName');
    const id = user.get('id');
    const currentPage = popup.getIn(['props', 'currentPage'], 'default');
    const defaultSelected = Map({ name: '', value: '', index: -1 });
    const assignedTo = popup.getIn(['props', 'assignedTo'], {
      suggestions: [],
      text: userName,
      selected: { text: userName, id },
    });

    updatePopupFormData({
      date: moment(),
      override: false,
      passed: false,
      noResponse: currentPage === 'browse',
      backlog: false,
      approved: false,
      activity: Map(),
      assignedTo,
      status: {
        defaultSelected,
        index: defaultSelected.get('index'),
      },
      formError: List(),
    });

    fetchTargetStatuses(({ response: { data } }) => {
      setStatuses(targetStatusesWithDefault(fromJS(data)));
    });
    fetchEventActivities();
  }, []);

  const handleSuccessResponse = useCallback(
    ({ skipB, skipM, skipP, skipRO, skipTC, skipE, skipBA, skipDAB, skipBE, skipBN, skipRF }) => {
      const header = 'Warning';
      const popupMessages = [];

      const buildSkipMessages = (items, singleTitle, pluralTitle, useError = false) => {
        if (!items.length) return;

        const title = items.length === 1 ? singleTitle : pluralTitle;
        let skipMessage = '';

        items.forEach(error => {
          const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
          const line = useError ? `${error.error} ${link}\n` : `${error.companyName} ${link}\n`;

          skipMessage = `${skipMessage} ${line}`;
        });
        popupMessages.push(`${title}\n ${skipMessage}`);
      };

      buildSkipMessages(
        skipM,
        `The company was skipped because the merger of the companies had not been approved`,
        `The companies were skipped because the merger of the companies had not been approved`,
      );
      buildSkipMessages(
        skipBA,
        `The company was skipped because it was not Approved`,
        `The companies were skipped because they were not Approved`,
      );
      buildSkipMessages(
        skipDAB,
        `The company was skipped because it have a different Active Buyer`,
        `The companies were skipped because they have a different Active Buyer`,
      );
      buildSkipMessages(
        skipBE,
        `The company was skipped because it was not Excluded`,
        `The companies were skipped because they were not Excluded`,
      );
      buildSkipMessages(
        skipBN,
        `The company was skipped because the Approval field was not blank`,
        `The companies were skipped because the Approval field was not blank`,
      );
      buildSkipMessages(
        skipB,
        `The company was skipped because it has no active buyer`,
        `The companies were skipped because they had no active buyer`,
      );
      buildSkipMessages(
        skipRO,
        `The company was skipped because it was missing primary deal maker info`,
        `The companies were skipped because they were missing primary deal maker info`,
      );
      buildSkipMessages(
        skipTC,
        `The company has next actions successfully created, but it wasn't backlogged yet due to missing contact info. It will need to be fixed and backlogged manually`,
        `The companies had next actions successfully created, but they weren't backlogged yet due to missing contact info. They will need to be fixed and backlogged manually`,
      );
      buildSkipMessages(
        skipP,
        `The company was skipped because it has no active project`,
        `The companies were skipped because they had no active project`,
      );
      buildSkipMessages(
        skipE,
        `The company encountered internal database errors and should be reviewed\n`,
        `The companies encountered internal database errors and should be reviewed\n`,
        true,
      );

      if (skipRF.length) {
        const missingTitle =
          'Backlogging Failed due to missing data, and no targets were Backlogged. The following profiles were missing information:\n';
        let skipMessage = '';

        skipRF.forEach(error => {
          let errors = '';
          const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
          const companyName = `${error.companyName} ${link}\n`;

          error.emptyRequiredFields.forEach(message => {
            errors += `- ${message}.\n`;
          });
          skipMessage = `${skipMessage} ${companyName} ${errors}`;
        });
        popupMessages.push(`${missingTitle} ${skipMessage}`);
      }

      if (popupMessages.length > 0) {
        showInformationCustomTitle(openPopup, header, popupMessages, () => {
          closePopup();
        });
      }

      return popupMessages;
    },
    [],
  );

  const isFormValid = useCallback(() => {
    const assignedTo = popup.getIn(['formData', 'assignedTo', 'selected', 'id']);
    const activity = popup.getIn(['formData', 'activity', 'selected', 'value']);
    const error = [];

    if (!assignedTo) {
      error.push('Assigned To field is required.');
    }

    if (!activity) {
      error.push('Activity field is required.');
    }
    updatePopupFormData({ formError: List(error) });

    return error.length === 0;
  }, [popup, updatePopupFormData]);

  const handleChange = useCallback(
    event => {
      if (event.target) {
        const { name, type, checked, value } = event.target;

        updatePopupFormData({
          [name]: type.toUpperCase() === 'CHECKBOX' ? checked : value,
          formError: List(),
        });
      } else {
        updatePopupFormData(event);
      }
    },
    [updatePopupFormData],
  );

  const handleActivityChange = useCallback(
    data => {
      updatePopupFormData({
        activity: data,
        formError: List(),
      });
    },
    [updatePopupFormData],
  );

  const handleDateChange = useCallback(
    ({ target }) => {
      if (target.value.isValid()) {
        updatePopupFormData({
          [target.name]: target.value,
          formError: List(),
        });
      }
    },
    [updatePopupFormData],
  );

  const handleStatusChange = useCallback(
    event => {
      const { value } = event.target;
      const index = statuses.findIndex(status => String(status.get('value')) === value);
      const selected = statuses.get(index);

      updatePopupFormData({
        status: {
          selected,
          index: selected.get('index'),
        },
        formError: List(),
      });
    },
    [statuses, updatePopupFormData],
  );

  const handleUpdate = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    if (isFormValid()) {
      const onUpdate = popup.getIn(['props', 'onUpdate'], () => {});
      const onSuccessResponse = popup.getIn(['props', 'onSuccessResponse'], handleSuccessResponse);
      const refreshGrid = popup.getIn(['props', 'refreshGrid'], () => {});

      onUpdate(popup.get('formData'), {
        beforeStart: () => {
          setSubmitting(true);
          startPopupLoading();
        },
        afterSuccess: ({ response }) => {
          setSubmitting(false);
          endPopupLoading();
          onSuccessResponse(response.skipped);
          refreshGrid();
        },
        afterError: ({ responseBody }) => {
          setSubmitting(false);
          endPopupLoading(responseBody);
        },
      });
    }
  }, [popup, isFormValid, isSubmitting, handleSuccessResponse, startPopupLoading, endPopupLoading]);

  const handleDelete = useCallback(() => {
    const onDelete = popup.getIn(['props', 'onDelete'], () => {});
    const refreshGrid = popup.getIn(['props', 'refreshGrid'], () => {});

    openPopup('ConfirmPopup', {
      message: `You are about to DELETE the Next Actions for the selected targets.
                Are you sure you want to proceed?`,
      onOk: () => {
        onDelete({
          afterSuccess: () => {
            closePopup();
            refreshGrid();
          },
        });
      },
      onCancel: () => closePopup(),
      yes: 'Yes',
      no: 'No',
    });
  }, [popup, openPopup, closePopup]);

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleUpdateSuggestions = useCallback(
    update => {
      updatePopupFormData({
        assignedTo: update,
        formError: List(),
      });
    },
    [updatePopupFormData],
  );

  const handleGetSuggestionValue = useCallback(suggestion => suggestion.text, []);

  const handleSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      updatePopupFormData({
        assignedTo: {
          text: suggestion.text,
          suggestions: [],
          selected: suggestion,
        },
      });
    },
    [updatePopupFormData],
  );

  const handleRenderSuggestion = useCallback(suggestion => <div>{suggestion.text}</div>, []);

  const handleGetSuggestion = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion(value, ({ response }) => {
        updatePopupFormData({
          assignedTo: {
            suggestions: response.data,
          },
        });
      });
    },
    [handleGetUsersSuggestion, updatePopupFormData],
  );

  const currentPage = popup.getIn(['props', 'currentPage'], 'default');
  const formData = popup.get('formData');
  const suggestions = popup.getIn(['formData', 'assignedTo', 'suggestions'], new List());
  const text = popup.getIn(['formData', 'assignedTo', 'text'], '');
  const activityGroup = formData.getIn(['activity', 'selected', 'group'], 5);

  const assignedToSuggestionsProps = {
    inputProps: {
      className: 'form-control',
      id: 'assignToDropdown',
      name: 'assignToDropdown',
      label: 'Assigned To',
      disabled: false,
      placeholder: '',
    },
    suggestions,
    onUpdateSuggestions: handleUpdateSuggestions,
    getSuggestion: handleGetSuggestion,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleSuggestionSelected,
    text,
  };

  const descriptionHidden =
    activityGroup < 6 ||
    activityGroup === 7 ||
    (activityGroup === 9 && formData.getIn(['activity', 'selected', 'value']) === 'HarvCo Meeting');
  const backlogHidden = activityGroup > 4 && activityGroup !== 7;
  const applyToHidden = currentPage === 'browse';

  return (
    <SetNextActionsPopup
      activities={activities}
      applyToHidden={applyToHidden}
      assignedToSuggestions={assignedToSuggestionsProps}
      backlogHidden={backlogHidden}
      currentPage={currentPage}
      descriptionHidden={descriptionHidden}
      formData={formData}
      formError={formData.get('formError')}
      isPopupFetching={popup.get('isPopupFetching')}
      isPopupFetchingError={popup.get('isPopupFetchingError')}
      isSubmitting={isSubmitting}
      onActivityChange={handleActivityChange}
      onChange={handleChange}
      onClose={handleClose}
      onDateChange={handleDateChange}
      onDelete={handleDelete}
      onStatusChange={handleStatusChange}
      onUpdate={handleUpdate}
      statuses={statuses}
      user={user}
    />
  );
};

SetNextActionsPopupContainer.propTypes = {
  popup: PropTypes.instanceOf(Map).isRequired,
};

export { SetNextActionsPopupContainer };
export default connect(
  (state, props) => ({
    ...props,
    user: state.auth.get('user', null),
    activities: state.eventActivities.getIn(['activities', 'all', 'target'], List()),
  }),
  { fetchTargetStatuses, fetchEventActivities, handleGetUsersSuggestion },
  mergeProps,
  connectOptions,
)(memo(SetNextActionsPopupContainer));
